import cn from 'classnames'
import { useMemo } from 'react'

import { GrowField } from '../../../components/GrowField'
import { formatMoneyInteger } from '../../../helpers/formatMoneyInteger'
import { IS_HIDE_QUOTA_GROW, PERCENT_FRACTION_DIGITS } from '../../config'

import { IExtraRowsProps } from './models/IExtraRowsProps'

import styles from './ExtraFinRows.module.scss'

interface IExtraFinRowsProps extends IExtraRowsProps {
  isDistributorPlanner: boolean
  isShowPartnerCount: boolean
  isShowYoYPartnersVsPartners: boolean
  isShowQoQPartnersVsPartners: boolean

  isShowPlanAchievement: boolean
  isShowQuotaAchievement: boolean

  partnerCount: number
  partnerCountPercent: number
  partnerCountYoY?: number
  partnerCountQoQ?: number

  yoyQuota?: number

  yoyTargetsAch?: number
  yoyQuotaAch?: number

  qoqQuota?: number
}

export const ExtraFinRows = ({
  isPlanningByFY,
  sType,

  isDistributorPlanner,
  isShowPartnerCount,
  isShowYoYPartnersVsPartners,
  isShowQoQPartnersVsPartners,

  isShowYoYActualsVsActuals,
  isShowYoYActualsVsPlans,
  isShowYoYPlansVsPlans,
  isShowYoYQuotaVsActuals,

  isShowPlanAchievement,
  isShowQuotaAchievement,

  isShowQoQActualsVsActuals,
  isShowQoQActualsVsPlans,
  isShowQoQPlansVsPlans,
  isShowQoQQuotaVsActuals,

  partnerCount,
  partnerCountPercent,
  partnerCountYoY,
  partnerCountQoQ,

  yoyActuals,
  yoyTargets,
  yoyPlans,
  yoyQuota,

  yoyTargetsAch,
  yoyQuotaAch,

  qoqActuals,
  qoqTargets,
  qoqPlans,
  qoqQuota,
}: IExtraFinRowsProps) => {
  /** -------------------- Variables ----------------------- */

  const isShowAnyYoY = useMemo(() => {
    return (
      isShowYoYActualsVsActuals ||
      isShowYoYActualsVsPlans ||
      isShowYoYPlansVsPlans ||
      isShowYoYQuotaVsActuals
    )
  }, [
    isShowYoYActualsVsActuals,
    isShowYoYActualsVsPlans,
    isShowYoYPlansVsPlans,
    isShowYoYQuotaVsActuals,
  ])

  const isShowAnyAchievement = useMemo(() => {
    return isShowPlanAchievement || isShowQuotaAchievement
  }, [isShowPlanAchievement, isShowQuotaAchievement])

  const isShowAnyQoQ = useMemo(() => {
    return (
      isShowQoQActualsVsActuals ||
      isShowQoQActualsVsPlans ||
      isShowQoQPlansVsPlans ||
      isShowQoQQuotaVsActuals
    )
  }, [
    isShowQoQActualsVsActuals,
    isShowQoQActualsVsPlans,
    isShowQoQPlansVsPlans,
    isShowQoQQuotaVsActuals,
  ])

  const isShowAnyPartnerCount = useMemo(() => {
    return isDistributorPlanner && isShowPartnerCount
  }, [isDistributorPlanner, isShowPartnerCount])

  const isShowCountPercent = useMemo(() => {
    return isDistributorPlanner && isShowPartnerCount && !!sType
  }, [isDistributorPlanner, isShowPartnerCount, sType])

  const formattedPartnerCount = useMemo(() => {
    let formattedValue = '-'

    if (partnerCount) {
      formattedValue = formatMoneyInteger(partnerCount, 0, 1)
    }

    return formattedValue
  }, [partnerCount])

  /** -------------------- Render ----------------------- */

  return (
    <div>
      {isShowAnyPartnerCount && (
        <div className={styles.yoyBlock}>
          <div className={styles.actualsColor}>{formattedPartnerCount}</div>
          {isShowCountPercent && (
            <>
              {partnerCountPercent ? (
                <GrowField
                  percent={partnerCountPercent}
                  achiev
                  isNoColor
                  fractionDigits={PERCENT_FRACTION_DIGITS}
                />
              ) : (
                '-'
              )}
            </>
          )}
          {isShowYoYPartnersVsPartners && <div>{partnerCountYoY ? partnerCountYoY : '-'}</div>}
          {isShowQoQPartnersVsPartners && <div>{partnerCountQoQ ? partnerCountQoQ : '-'}</div>}
        </div>
      )}

      <div className={cn({ [styles.yoyBlock]: isShowAnyYoY })}>
        {isShowYoYActualsVsActuals && (
          <GrowField
            percent={yoyActuals}
            isPaleColor={true}
            fractionDigits={PERCENT_FRACTION_DIGITS}
          />
        )}
        {isShowYoYActualsVsPlans && (
          <GrowField percent={yoyTargets} fractionDigits={PERCENT_FRACTION_DIGITS} />
        )}
        {isShowYoYPlansVsPlans && (
          <GrowField percent={yoyPlans} fractionDigits={PERCENT_FRACTION_DIGITS} />
        )}
        {isShowYoYQuotaVsActuals && (
          <GrowField
            percent={yoyQuota}
            isPaleColor={true}
            isEmpty={IS_HIDE_QUOTA_GROW}
            fractionDigits={PERCENT_FRACTION_DIGITS}
          />
        )}
      </div>

      <div className={cn({ [styles.yoyBlock]: isShowAnyAchievement })}>
        {isShowPlanAchievement && (
          <GrowField percent={yoyTargetsAch} achiev fractionDigits={PERCENT_FRACTION_DIGITS} />
        )}
        {isShowQuotaAchievement && (
          <GrowField
            percent={yoyQuotaAch}
            achiev
            isPaleColor={true}
            fractionDigits={PERCENT_FRACTION_DIGITS}
          />
        )}
      </div>

      <div className={cn({ [styles.yoyBlock]: isShowAnyQoQ })}>
        {isPlanningByFY && (
          <div>
            {isShowQoQActualsVsActuals && <div>&nbsp;</div>}
            {isShowQoQActualsVsPlans && <div>&nbsp;</div>}
            {isShowQoQPlansVsPlans && <div>&nbsp;</div>}
            {isShowQoQQuotaVsActuals && <div>&nbsp;</div>}
          </div>
        )}
        {!isPlanningByFY && (
          <>
            {isShowQoQActualsVsActuals && (
              <GrowField
                percent={qoqActuals}
                isPaleColor={true}
                fractionDigits={PERCENT_FRACTION_DIGITS}
              />
            )}
            {isShowQoQActualsVsPlans && (
              <GrowField percent={qoqTargets} fractionDigits={PERCENT_FRACTION_DIGITS} />
            )}
            {isShowQoQPlansVsPlans && (
              <GrowField percent={qoqPlans} fractionDigits={PERCENT_FRACTION_DIGITS} />
            )}
            {isShowQoQQuotaVsActuals && (
              <GrowField
                percent={qoqQuota}
                isPaleColor={true}
                isEmpty={IS_HIDE_QUOTA_GROW}
                fractionDigits={PERCENT_FRACTION_DIGITS}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
